import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import confetti from 'canvas-confetti';
import { 
  CheckCircle2, 
  AlertCircle, 
  Send, 
  RotateCcw, 
  ChevronRight, 
  ChevronLeft, 
  Sparkles, 
  Globe, 
  ShieldCheck, 
  UserCheck, 
  Award, 
  ExternalLink,
  Mail,
  User,
  Edit2,
  Cloud,
  Loader2
} from 'lucide-react';

export const VotingView: React.FC = () => {
  const {
    projects,
    criteria,
    settings,
    voterName,
    voterEmail,
    setVoterIdentity,
    voterRole,
    setVoterRole,
    draftScores,
    setScore,
    saveStatus,
    submitMyBallot,
    resetMyBallot,
    hasVoted,
    mySubmittedBallot,
    setActiveTab,
    isAdminAuthenticated,
  } = useApp();

  const [activeProjectIdx, setActiveProjectIdx] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [showIdentityModal, setShowIdentityModal] = useState<boolean>(!voterEmail.trim() || !voterName.trim());
  const [inputName, setInputName] = useState<string>(voterName);
  const [inputEmail, setInputEmail] = useState<string>(voterEmail);
  const [identityError, setIdentityError] = useState<string | null>(null);

  useEffect(() => {
    if (voterName && !inputName) setInputName(voterName);
    if (voterEmail && !inputEmail) setInputEmail(voterEmail);
    if (voterEmail.trim() && voterName.trim()) {
      setShowIdentityModal(false);
    }
  }, [voterName, voterEmail]);

  const handleSaveIdentity = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputName.trim()) {
      setIdentityError('Please enter your full name.');
      return;
    }
    const cleanEmail = inputEmail.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@') || !cleanEmail.includes('.')) {
      setIdentityError('Please enter a valid email address.');
      return;
    }
    setIdentityError(null);
    setVoterIdentity(inputName.trim(), cleanEmail);
    setShowIdentityModal(false);
  };

  const votingContainerRef = useRef<HTMLDivElement>(null);

  const scrollToVotingContainer = () => {
    setTimeout(() => {
      votingContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 30);
  };

  const activeProject = projects[activeProjectIdx] || projects[0];

  const triggerConfetti = () => {
    confetti({
      particleCount: 90,
      spread: 75,
      origin: { y: 0.6 },
      colors: ['#e10600', '#EBE9E5', '#D6D2CC', '#101820', '#f59e0b'],
    });
  };

  const handleFinalSubmit = () => {
    setErrorMessage(null);
    const result = submitMyBallot();
    if (result.success) {
      setShowReviewModal(false);
      triggerConfetti();
    } else {
      setErrorMessage(result.message || 'Error submitting ballot.');
    }
  };

  // Compute progress
  const completedProjectsCount = projects.filter((p) => {
    const scores = draftScores[p.id] || {};
    return criteria.every((c) => (scores[c.id] || 0) > 0);
  }).length;

  const isAllMinistriesCompleted = projects.length > 0 && completedProjectsCount === projects.length;

  // Render Submitted State Receipt
  if (hasVoted && mySubmittedBallot) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12 pb-24">
        <div className="bg-white border border-emerald-400 rounded-3xl p-6 sm:p-10 text-center shadow-lg relative overflow-hidden">
          <div className="w-16 h-16 rounded-2xl bg-emerald-100 border border-emerald-300 flex items-center justify-center mx-auto mb-4 text-emerald-600">
            <ShieldCheck className="w-8 h-8" />
          </div>

          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-2 border border-emerald-300">
            <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
            <span>Official Judge Ballot Recorded</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-black">
            Thank You, {mySubmittedBallot.voterName}!
          </h2>
          <p className="mt-2 text-sm text-brand-subtext max-w-md mx-auto">
            Your ratings have been officially submitted to determine the NEXT Ventures Shark Tank 2026 grant finalists!
          </p>

          {/* Receipt Info Card */}
          <div className="mt-8 p-5 rounded-2xl bg-[#F7F6F4] border border-brand-tan text-left max-w-lg mx-auto">
            <div className="flex items-center justify-between border-b border-brand-tan/60 pb-3 text-xs text-brand-subtext">
              <span>Judge Name & Email</span>
              <span className="text-brand-black font-semibold text-right">
                {mySubmittedBallot.voterName}
                {(mySubmittedBallot.voterEmail || voterEmail) && (
                  <span className="block text-[11px] text-brand-subtext font-normal">
                    {mySubmittedBallot.voterEmail || voterEmail}
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-brand-tan/60 py-3 text-xs text-brand-subtext">
              <span>Submitted At</span>
              <span className="text-brand-black font-medium">
                {new Date(mySubmittedBallot.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-brand-tan/60 py-3 text-xs text-brand-subtext">
              <span>Voter Classification</span>
              <span className={`font-bold px-2 py-0.5 rounded-full text-[11px] ${
                mySubmittedBallot.voterRole === 'staff'
                  ? 'bg-purple-100 text-purple-800 border border-purple-300'
                  : 'bg-brand-tan/40 text-brand-black'
              }`}>
                {mySubmittedBallot.voterRole === 'staff'
                  ? `Staff Member (${mySubmittedBallot.weight || settings.staffVoteWeight}x Weighted)`
                  : 'Audience Member (1x)'}
              </span>
            </div>
            <div className="flex items-center justify-between pt-3 text-xs text-brand-subtext">
              <span>Ministries Evaluated</span>
              <span className="font-bold text-emerald-700">
                {Object.keys(mySubmittedBallot.scores).length} of {projects.length} Ministries
              </span>
            </div>
          </div>

          {/* Quick Score Review */}
          <div className="mt-8 text-left max-w-lg mx-auto space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-brand-subtext">
              Your Evaluated Ministries
            </h4>
            <div className="divide-y divide-brand-tan rounded-xl border border-brand-tan bg-white overflow-hidden text-xs shadow-sm max-h-64 overflow-y-auto">
              {projects.map((proj) => {
                return (
                  <div key={proj.id} className="p-3 flex items-center justify-between">
                    <div>
                      <span className="font-bold text-brand-black">#{proj.order} {proj.title}</span>
                      <div className="text-[11px] text-brand-subtext">{proj.presenter}</div>
                    </div>
                    <span className="font-bold text-emerald-700 text-xs bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                      Evaluated
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => setActiveTab('projects')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-black hover:bg-black text-white text-sm font-bold rounded-xl shadow-md transition-all"
            >
              <span>Explore Ministries</span>
            </button>

            {settings.phase === 'VOTING_OPEN' && (
              <button
                onClick={resetMyBallot}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-3 bg-brand-tan/20 hover:bg-brand-tan/40 text-brand-black text-xs font-semibold rounded-xl border border-brand-tan transition-all"
              >
                <RotateCcw className="w-3.5 h-3.5 text-brand-subtext" />
                <span>Edit / Re-submit Ballot</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12 pb-28">
      {/* Official Judge Instructions & Grant Tiers Box */}
      <div className="bg-white border-2 border-brand-red/30 rounded-3xl p-6 sm:p-8 shadow-md mb-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-red/5 rounded-bl-full pointer-events-none" />

        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 text-brand-red font-bold text-xs uppercase tracking-wider">
            <Award className="w-4 h-4" />
            <span>Judges Official Voting Brief</span>
          </div>
        </div>

        <h1 className="text-2xl sm:text-3xl font-extrabold text-brand-black tracking-tight mb-3">
          NEXT Ventures Shark Tank 2026 Judges
        </h1>

        <p className="text-sm sm:text-base text-brand-black font-semibold mb-4 leading-snug">
          It's time to choose your top picks for our grant finalists!
        </p>

        {/* Prize Tiers Badges Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-5">
          {settings.prizeTiers.map((tier) => (
            <div
              key={tier.rank}
              className={`p-2.5 rounded-xl border text-center transition-all ${
                tier.rank === 1
                  ? 'bg-brand-red text-white border-brand-red shadow-sm'
                  : 'bg-[#F7F6F4] border-brand-tan text-brand-black'
              }`}
            >
              <div className={`text-[10px] font-bold uppercase tracking-wider ${tier.rank === 1 ? 'text-white/80' : 'text-brand-subtext'}`}>
                {tier.place}
              </div>
              <div className="text-base font-black mt-0.5">{tier.amount}</div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F6F4] border border-brand-tan rounded-2xl p-4 text-xs sm:text-sm text-brand-black space-y-2">
          <p className="font-semibold text-brand-black">
            Please judge each ministry using our 7 criteria areas on a scale of 1–5:
          </p>
          <div className="flex flex-wrap gap-4 text-xs text-brand-subtext font-medium pt-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <strong>5:</strong> Amazing
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <strong>3:</strong> Average
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
              <strong>1:</strong> Below Average
            </span>
          </div>
          <p className="text-xs text-brand-subtext pt-1 leading-relaxed">
            Using your ratings for each ministry and each area below, we will compile the voting from NEXT Ventures Shark Tank 2026 Judges with 1st place going to the highest score, etc. Thank you for your support of NEXT Ventures and for your participation in this vote!
          </p>
        </div>
      </div>

      {/* Voter Identification Card */}
      <div className="bg-white border border-brand-tan p-5 sm:p-6 rounded-3xl mb-8 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-brand-red/10 border border-brand-red/20 flex items-center justify-center text-brand-red shrink-0">
              <UserCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-brand-subtext">
                  Registered Judge
                </span>
                {voterEmail.trim() && voterName.trim() ? (
                  <>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-[11px] font-semibold text-emerald-700">Active</span>
                    <span className="text-brand-tan/80 text-xs">•</span>
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-subtext">
                      {saveStatus === 'saving' ? (
                        <>
                          <Loader2 className="w-3 h-3 text-brand-red animate-spin" />
                          <span className="text-brand-red font-semibold">Saving changes...</span>
                        </>
                      ) : saveStatus === 'saved' ? (
                        <>
                          <Cloud className="w-3 h-3 text-emerald-600" />
                          <span className="text-emerald-700 font-semibold">Saved to cloud</span>
                        </>
                      ) : (
                        <>
                          <Cloud className="w-3 h-3 text-brand-subtext" />
                          <span>Auto-save active</span>
                        </>
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    <span className="text-[11px] font-semibold text-amber-700">Setup Required</span>
                  </>
                )}
              </div>
              <div className="text-base font-extrabold text-brand-black flex flex-wrap items-center gap-1.5 sm:gap-2">
                <span>{voterName.trim() || 'Attendee Name Not Set'}</span>
                {voterEmail.trim() && (
                  <span className="text-xs font-normal text-brand-subtext">
                    ({voterEmail})
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setInputName(voterName);
              setInputEmail(voterEmail);
              setIdentityError(null);
              setShowIdentityModal(true);
            }}
            className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#F7F6F4] hover:bg-brand-tan/40 border border-brand-tan text-xs font-semibold text-brand-black transition-all"
          >
            <Edit2 className="w-3.5 h-3.5 text-brand-subtext" />
            <span>{voterEmail.trim() && voterName.trim() ? 'Edit Info' : 'Set Name & Email'}</span>
          </button>
        </div>

        {/* Staff Voting Control (Hidden unless authenticated in Admin) */}
        {isAdminAuthenticated && (
          <div className="pt-4 border-t border-brand-tan/60">
            <div className="p-4 rounded-2xl bg-purple-50 border border-purple-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-bold text-purple-950 flex items-center gap-2">
                    <span>Admin Logged In • Staff Weighted Voting</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-200 text-purple-900 font-extrabold">
                      {settings.staffVoteWeight || 3}x Multiplier
                    </span>
                  </div>
                  <p className="text-[11px] text-purple-800/90 mt-0.5">
                    Currently voting as: <strong>{voterRole === 'staff' ? `Exponential Staff (${settings.staffVoteWeight || 3}x Weight)` : 'Standard Audience (1x)'}</strong>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setVoterRole(voterRole === 'staff' ? 'audience' : 'staff')}
                  className="px-3.5 py-1.5 rounded-xl border text-xs font-semibold transition-all bg-white hover:bg-purple-100/60 border-purple-300 text-purple-900 shadow-2xs"
                >
                  {voterRole === 'staff' ? 'Switch to 1x Audience' : 'Activate 3x Staff Ballot'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ministry Pitch Selector Pills (1 to 12) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold uppercase tracking-wider text-brand-subtext">
            Evaluate Ministries ({activeProjectIdx + 1} of {projects.length})
          </span>
          <span className="text-xs font-semibold text-brand-black">
            Completed: <strong className="text-brand-red">{completedProjectsCount}</strong>/{projects.length}
          </span>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
          {projects.map((proj, idx) => {
            const pScores = draftScores[proj.id] || {};
            const isCompleted = criteria.every((c) => (pScores[c.id] || 0) > 0);
            const hasAnyScore = Object.values(pScores).some((s) => (s || 0) > 0);
            const isSelected = activeProjectIdx === idx;

            return (
              <button
                key={proj.id}
                onClick={() => {
                  setActiveProjectIdx(idx);
                  scrollToVotingContainer();
                }}
                className={`flex-1 min-w-[130px] p-2.5 rounded-xl border text-left transition-all shrink-0 shadow-sm ${
                  isSelected
                    ? 'bg-white border-brand-red ring-1 ring-brand-red/40 shadow-md'
                    : 'bg-white border-brand-tan hover:border-brand-warmgray'
                }`}
              >
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className={`font-extrabold ${isSelected ? 'text-brand-red' : 'text-brand-subtext'}`}>
                    #{proj.order}
                  </span>
                  {isCompleted ? (
                    <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded-md flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Voted
                    </span>
                  ) : hasAnyScore ? (
                    <span className="text-[10px] text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-md font-semibold">
                      In Progress
                    </span>
                  ) : (
                    <span className="text-[10px] text-brand-warmgray font-semibold">—</span>
                  )}
                </div>
                <div className="text-xs font-bold text-brand-black truncate">
                  {proj.title}
                </div>
                <div className="text-[10px] text-brand-subtext truncate">
                  {proj.presenter}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Ministry Evaluation Card */}
      {activeProject && (
        <div 
          ref={votingContainerRef}
          id="voting-container"
          className="bg-white border border-brand-tan rounded-3xl p-6 sm:p-8 shadow-sm mb-8 relative scroll-mt-20 sm:scroll-mt-24"
        >
          {/* Header Strip: Ministry Name & Presenter Name */}
          <div className="border-b border-brand-tan pb-5 mb-6">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs font-extrabold px-2.5 py-0.5 rounded-full bg-brand-red/10 text-brand-red border border-brand-red/20">
                Ministry #{activeProject.order} of {projects.length}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-black">
                  {activeProject.title}
                </h2>
                <p className="text-sm font-semibold text-brand-red mt-0.5">
                  Presenter: <span className="text-brand-black font-bold">{activeProject.presenter}</span>
                </p>
              </div>

              {activeProject.website && (
                <a
                  href={activeProject.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-brand-subtext hover:text-brand-red font-medium transition-colors"
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>{activeProject.website.replace(/^https?:\/\//, '')}</span>
                  <ExternalLink className="w-3 h-3 opacity-70" />
                </a>
              )}
            </div>

            {/* Ministry Purpose */}
            <div className="mt-4 p-3.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-xs sm:text-sm text-brand-black leading-relaxed">
              <strong className="text-brand-red font-bold">Purpose: </strong>
              <span>{activeProject.purpose}</span>
            </div>
          </div>

          {/* 7 Voting Criteria on 1-5 Scale */}
          <div className="space-y-6">
            <div className="flex items-center justify-between pb-1 border-b border-brand-tan/50">
              <h3 className="text-xs font-bold uppercase tracking-wider text-brand-subtext">
                Rate on 7 Official Criteria (Scale 1–5)
              </h3>
              <span className="text-xs font-semibold text-brand-subtext">
                <strong className="text-brand-black font-bold">
                  {criteria.filter((c) => (draftScores[activeProject.id]?.[c.id] || 0) > 0).length}
                </strong> of {criteria.length} rated
              </span>
            </div>

            {criteria.map((criterion, critIdx) => {
              const currentScore = draftScores[activeProject.id]?.[criterion.id] || 0;

              return (
                <div
                  key={criterion.id}
                  className="bg-[#F7F6F4] border border-brand-tan rounded-2xl p-4 transition-all hover:border-brand-warmgray shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-brand-red/15 text-brand-red text-xs font-bold flex items-center justify-center">
                          {critIdx + 1}
                        </span>
                        <h4 className="text-sm font-extrabold text-brand-black">
                          {criterion.name}
                        </h4>
                      </div>
                      <p className="text-xs text-brand-subtext mt-1 font-medium leading-relaxed">
                        {criterion.question}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <span className="text-xs font-bold text-brand-black">
                        {currentScore > 0 ? (
                          <span className="text-brand-red font-extrabold text-base">
                            {currentScore} <span className="text-xs text-brand-subtext font-normal">/ 5</span>
                          </span>
                        ) : (
                          <span className="text-brand-subtext/70 italic text-xs">Not rated</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* 1 - 5 Rating Buttons */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-5 gap-2 sm:gap-3">
                      {[1, 2, 3, 4, 5].map((scoreVal) => {
                        const isSelected = currentScore === scoreVal;
                        return (
                          <button
                            key={scoreVal}
                            type="button"
                            onClick={() => setScore(activeProject.id, criterion.id, scoreVal)}
                            className={`py-3 sm:py-3.5 rounded-xl font-display font-bold text-base sm:text-lg transition-all flex flex-col items-center justify-center ${
                              isSelected
                                ? 'bg-brand-red text-white shadow-md shadow-brand-red/30 scale-[1.03] ring-2 ring-brand-red/20'
                                : 'bg-white text-brand-black hover:bg-brand-red/5 hover:border-brand-red/40 border border-brand-tan shadow-sm'
                            }`}
                          >
                            <span>{scoreVal}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Scale Guide: 1 below average • 3 average • 5 amazing */}
                    <div className="flex justify-between items-center text-[11px] text-brand-subtext px-1 pt-1 font-medium">
                      <span>1: Below average</span>
                      <span>3: Average</span>
                      <span>5: Amazing</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stepper Navigation Buttons */}
          <div className="mt-8 pt-6 border-t border-brand-tan flex items-center justify-between gap-3">
            <button
              onClick={() => {
                setActiveProjectIdx(Math.max(0, activeProjectIdx - 1));
                scrollToVotingContainer();
              }}
              disabled={activeProjectIdx === 0}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                activeProjectIdx === 0
                  ? 'opacity-40 cursor-not-allowed text-brand-subtext'
                  : 'bg-brand-tan/20 hover:bg-brand-tan/40 text-brand-black border border-brand-tan'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous Ministry</span>
            </button>

            {activeProjectIdx < projects.length - 1 ? (
              <button
                onClick={() => {
                  setActiveProjectIdx(activeProjectIdx + 1);
                  scrollToVotingContainer();
                }}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-brand-red hover:bg-red-600 text-white text-xs sm:text-sm font-bold rounded-xl shadow-sm transition-all"
              >
                <span>Next Ministry</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => setShowReviewModal(true)}
                className={`inline-flex items-center gap-1.5 px-5 py-2.5 text-xs sm:text-sm font-bold rounded-xl shadow-md transition-all ${
                  isAllMinistriesCompleted
                    ? 'bg-brand-red hover:bg-red-600 text-white shadow-brand-red/30 hover:scale-105'
                    : 'bg-brand-black text-white hover:bg-neutral-800'
                }`}
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>{isAllMinistriesCompleted ? 'Review & Submit Official Ballot' : 'Review Ballot Progress'}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Sticky Bottom Bar: Ballot Progress & Review Submit Button */}
      <div className="fixed bottom-16 md:bottom-6 left-4 right-4 max-w-2xl mx-auto z-30">
        <div className="bg-white/95 border border-brand-tan p-4 rounded-2xl shadow-xl backdrop-blur-md flex items-center justify-between gap-4">
          <div>
            <div className="text-[11px] text-brand-subtext uppercase font-semibold">Judge Ballot Status</div>
            <div className="text-sm font-bold text-brand-black flex items-center gap-2">
              <span>{completedProjectsCount} of {projects.length} Ministries Completed</span>
              {isAllMinistriesCompleted ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold border border-emerald-300">
                  Ready to Submit
                </span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold border border-amber-300">
                  {projects.length - completedProjectsCount} remaining
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 text-[11px]">
              {saveStatus === 'saving' ? (
                <span className="inline-flex items-center gap-1 text-brand-red font-medium">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving changes...
                </span>
              ) : saveStatus === 'saved' ? (
                <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                  <Cloud className="w-3 h-3" />
                  Draft saved to cloud
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-brand-subtext">
                  <Cloud className="w-3 h-3 opacity-60" />
                  Auto-saved on change
                </span>
              )}
            </div>
          </div>

          <button
            onClick={() => setShowReviewModal(true)}
            className={`px-5 py-2.5 text-xs sm:text-sm font-bold rounded-xl shadow-md flex items-center gap-2 shrink-0 transition-all ${
              isAllMinistriesCompleted
                ? 'bg-brand-red hover:bg-red-600 text-white shadow-brand-red/20 hover:scale-105'
                : 'bg-brand-black hover:bg-neutral-800 text-white shadow-sm'
            }`}
          >
            <Send className="w-4 h-4" />
            <span>{isAllMinistriesCompleted ? 'Review & Submit' : 'Review Progress'}</span>
          </button>
        </div>
      </div>

      {/* Review & Confirmation Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm overflow-y-auto">
          <div 
            className="w-full max-w-lg bg-white border border-brand-tan rounded-2xl shadow-2xl p-6 text-brand-black my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-brand-tan pb-4 mb-4">
              <div>
                <h3 className="text-xl font-bold text-brand-black">Confirm Judge Ballot</h3>
                <p className="text-xs text-brand-subtext mt-0.5">
                  Verify your evaluations across all {projects.length} ministries before submitting.
                </p>
              </div>
              <button
                onClick={() => setShowReviewModal(false)}
                className="text-brand-subtext hover:text-brand-black"
              >
                ✕
              </button>
            </div>

            {errorMessage && (
              <div className="mb-4 p-3 rounded-xl bg-brand-red/10 border border-brand-red/40 text-brand-red text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {!isAllMinistriesCompleted && (
              <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
                <span>You must fully vote for all {projects.length} ministries before submitting.</span>
              </div>
            )}

            {/* Voter Verification in Modal */}
            <div className="mb-4 p-3.5 rounded-xl bg-[#F7F6F4] border border-brand-tan flex items-center justify-between">
              <div>
                <label className="block text-[10px] uppercase font-bold text-brand-subtext">
                  Voting As
                </label>
                <div className="text-sm font-bold text-brand-black">{voterName || 'Unnamed Voter'}</div>
                {voterEmail && (
                  <div className="text-xs text-brand-subtext font-mono">{voterEmail}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowReviewModal(false);
                  setInputName(voterName);
                  setInputEmail(voterEmail);
                  setIdentityError(null);
                  setShowIdentityModal(true);
                }}
                className="text-xs font-semibold text-brand-red hover:underline inline-flex items-center gap-1"
              >
                <Edit2 className="w-3 h-3" />
                <span>Change</span>
              </button>
            </div>

            {/* Ministry Breakdown List */}
            <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1 mb-6 divide-y divide-brand-tan">
              {projects.map((proj, idx) => {
                const projScores = draftScores[proj.id] || {};
                const isComplete = criteria.every((c) => (projScores[c.id] || 0) > 0);

                return (
                  <div key={proj.id} className="pt-2 pb-1 flex items-center justify-between text-xs">
                    <div>
                      <div className="font-bold text-brand-black flex items-center gap-1.5">
                        <span>#{proj.order} {proj.title}</span>
                      </div>
                      <div className="text-[11px] text-brand-subtext">{proj.presenter}</div>
                    </div>
                    <div>
                      {isComplete ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold text-[11px] border border-emerald-300">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          <span>Fully Voted</span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setActiveProjectIdx(idx);
                            setShowReviewModal(false);
                            scrollToVotingContainer();
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-red/10 hover:bg-brand-red/20 text-brand-red font-bold text-[11px] border border-brand-red/30 transition-colors"
                        >
                          <span>Vote Now</span>
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Completion Summary */}
            <div className="p-4 rounded-xl bg-[#F7F6F4] border border-brand-tan flex items-center justify-between mb-6">
              <div>
                <div className="text-xs text-brand-subtext">Voting Progress</div>
                <div className="text-sm sm:text-base font-bold text-brand-black">
                  {completedProjectsCount} of {projects.length} Ministries Completed
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-brand-subtext">Submission Eligibility</div>
                <div className={`text-xs font-extrabold ${isAllMinistriesCompleted ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {isAllMinistriesCompleted ? '✓ Eligible to Submit' : `All ${projects.length} Required`}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-2 text-xs font-semibold text-brand-subtext hover:text-brand-black"
              >
                Back to Edit
              </button>

              <button
                onClick={handleFinalSubmit}
                disabled={!isAllMinistriesCompleted}
                className={`px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all ${
                  isAllMinistriesCompleted
                    ? 'bg-brand-red hover:bg-red-600 text-white shadow-md shadow-brand-red/30 hover:scale-105 cursor-pointer'
                    : 'bg-brand-tan/50 text-brand-subtext border border-brand-tan cursor-not-allowed'
                }`}
              >
                <Send className="w-4 h-4" />
                <span>{isAllMinistriesCompleted ? 'Confirm & Submit Ballot' : `Vote All ${projects.length} to Submit`}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding / Voter Identity Modal */}
      {showIdentityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/75 backdrop-blur-md overflow-y-auto">
          <div 
            className="w-full max-w-md bg-white border border-brand-tan rounded-3xl shadow-2xl p-6 sm:p-7 text-brand-black my-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button only allowed if they already have an identity registered */}
            {voterEmail.trim() && voterName.trim() && (
              <button
                type="button"
                onClick={() => {
                  setIdentityError(null);
                  setShowIdentityModal(false);
                }}
                className="absolute top-5 right-5 p-2 rounded-xl text-brand-subtext hover:text-brand-black hover:bg-brand-tan/20 transition-colors"
                aria-label="Close modal"
              >
                ✕
              </button>
            )}

            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-brand-red/10 border border-brand-red/20 flex items-center justify-center mx-auto mb-3 text-brand-red">
                <UserCheck className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-extrabold text-brand-black tracking-tight">
                {voterEmail.trim() && voterName.trim() ? 'Update Judge Profile' : 'Welcome, Shark Tank Judge!'}
              </h3>
              <p className="text-xs sm:text-sm text-brand-subtext mt-1.5 leading-relaxed">
                Please enter your name and email. Your email is your unique identifier so your votes are saved and can be recovered if you close your page.
              </p>
            </div>

            {identityError && (
              <div className="mb-4 p-3 rounded-xl bg-brand-red/10 border border-brand-red/30 text-brand-red text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{identityError}</span>
              </div>
            )}

            <form onSubmit={handleSaveIdentity} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-brand-subtext mb-1">
                  Full Name <span className="text-brand-red">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-subtext" />
                  <input
                    type="text"
                    required
                    placeholder="e.g., Sarah Johnson"
                    value={inputName}
                    onChange={(e) => setInputName(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black placeholder:text-brand-warmgray text-sm focus:outline-none focus:border-brand-red/60 shadow-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-brand-subtext mb-1">
                  Email Address <span className="text-brand-red">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-subtext" />
                  <input
                    type="email"
                    required
                    placeholder="e.g., sarah@example.com"
                    value={inputEmail}
                    onChange={(e) => setInputEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan text-brand-black placeholder:text-brand-warmgray text-sm focus:outline-none focus:border-brand-red/60 shadow-sm"
                  />
                </div>
                <p className="text-[11px] text-brand-subtext mt-1">
                  Your votes and progress will be securely linked to this email.
                </p>
              </div>

              <div className="pt-2 flex flex-col gap-2">
                <button
                  type="submit"
                  className="w-full py-3 px-4 rounded-xl bg-brand-red hover:bg-red-600 text-white font-bold text-sm shadow-md shadow-brand-red/20 transition-all hover:scale-[1.02]"
                >
                  {voterEmail.trim() && voterName.trim() ? 'Save Profile' : 'Start Voting →'}
                </button>

                {(!voterEmail.trim() || !voterName.trim()) && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('projects')}
                    className="w-full py-2 text-xs font-semibold text-brand-subtext hover:text-brand-black transition-colors"
                  >
                    Explore Ministries First
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
